/**
 * Client-side image preparation BEFORE upload.
 *
 * Why: phone cameras produce 12-50 MP JPEGs (3-15 MB). Decoding them for the
 * preview and uploading them is slow and memory-hungry on mobile/TWA, and the
 * backend rejects > 12 MB. The backend itself only ever feeds a <= 1400 px copy
 * to the AI model, so downscaling to 2000 px on the long side loses nothing for
 * OCR while cutting memory and upload size by ~10x.
 *
 * Behaviour: returns a new JPEG File when the image is larger than MAX_SIDE or
 * SIZE_THRESHOLD; otherwise returns the original file untouched. Any failure
 * falls back to the original file so the existing pipeline is never blocked.
 */
export const MAX_SIDE = 2000;
export const JPEG_QUALITY = 0.88;
export const SIZE_THRESHOLD = 1.5 * 1024 * 1024; // 1.5 MB

async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      try { return await createImageBitmap(file); } catch { /* fall through */ }
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function dims(src) {
  return { w: src.width || src.naturalWidth || src.videoWidth, h: src.height || src.naturalHeight || src.videoHeight };
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/** Draw `source` (ImageBitmap | HTMLImageElement | HTMLVideoElement) scaled to MAX_SIDE and return a JPEG File. */
export async function sourceToJpegFile(source, filename = "paragon.jpg", maxSide = MAX_SIDE, quality = JPEG_QUALITY) {
  const { w, h } = dims(source);
  if (!w || !h) throw new Error("empty_source");
  const scale = Math.min(1, maxSide / Math.max(w, h));
  const tw = Math.max(1, Math.round(w * scale));
  const th = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = tw; canvas.height = th;
  const ctx = canvas.getContext("2d", { alpha: false });
  ctx.drawImage(source, 0, 0, tw, th);
  const blob = await canvasToBlob(canvas, "image/jpeg", quality);
  canvas.width = canvas.height = 0; // release bitmap memory eagerly
  if (!blob) throw new Error("encode_failed");
  return new File([blob], filename, { type: "image/jpeg", lastModified: Date.now() });
}

export async function prepareImageForUpload(file) {
  if (!file || !/^image\/(png|jpe?g|webp)$/i.test(file.type)) return file;
  let bitmap = null;
  try {
    bitmap = await decode(file);
    const { w, h } = dims(bitmap);
    const needsResize = Math.max(w, h) > MAX_SIDE || file.size > SIZE_THRESHOLD;
    if (!needsResize) return file;
    const name = (file.name || "paragon").replace(/\.[^.]+$/, "") + ".jpg";
    const out = await sourceToJpegFile(bitmap, name);
    // Only use the resized version if it is actually smaller; otherwise keep the original.
    return out.size < file.size ? out : file;
  } catch {
    return file;
  } finally {
    if (bitmap && typeof bitmap.close === "function") bitmap.close();
  }
}

/** True when an in-page camera (getUserMedia) is available in this context. */
export function canUseInAppCamera() {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function" && (typeof window === "undefined" || window.isSecureContext !== false);
}
